import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { MPaymentTransactionEntity } from './m-payment-transaction.entity';
import { OrderEntity } from './order.entity';
import { PaymentProcessorConfigEntity } from './payment-processor-config.entity';

@Index('m_sm_payment_fk_client__id', ['mSmPaymentFkClientId'], {})
@Index(
	'IDX_m_payment_payment_method_fk_payment_processor_config__id',
	['fkPaymentProcessorConfigId'],
	{},
)
@Entity('m_payment_payment_method', {}, { label: 'Payment Method' })
export class MPaymentPaymentMethodEntity extends BaseEntity {
	@Column('varchar', {
		name: 'm_payment_externalId_paymentProcessor',
		nullable: true,
		length: 64,
	})
	public mPaymentExternalIdPaymentProcessor!: string | null;

	@Column('varchar', {
		name: 'm_payment_externalId_erp',
		nullable: true,
		length: 64,
	})
	public mPaymentExternalIdErp!: string | null;

	@Column('char', { name: 'm_payment_lastFour', nullable: true, length: 4 })
	public mPaymentLastFour!: string | null;

	@Column('char', {
		name: 'm_payment_routingNumber',
		nullable: true,
		length: 9,
	})
	public mPaymentRoutingNumber!: string | null;

	@Column('varchar', { name: 'm_payment_type', length: 64 })
	public mPaymentType!: string;

	/**
	 * The card BRAND — Visa, MasterCard, Amex — or the bank-account kind for
	 * ACH. It says nothing about how a card is funded: a Visa here may be
	 * credit, debit or prepaid. For the surcharge decision read
	 * `mPaymentCardCategory` below, never this.
	 */
	@Column('varchar', { name: 'm_payment_subtype', nullable: true, length: 64 })
	public mPaymentSubtype!: string | null;

	/**
	 * PD-15142 — how this card is funded: `credit`, `debit`, `prepaid`,
	 * `charge`, `deferred_debit`, `unknown` or `unavailable`. Null for ACH and
	 * for cards stored before this was captured.
	 *
	 * This decides whether a processing fee may be charged at all. Regulation II
	 * prohibits surcharging debit — including signature debit run as credit —
	 * and the networks extend that to prepaid, so ONLY `credit` is
	 * surchargeable. Anything unresolved gets no fee: absorbing 3% is a cost,
	 * while surcharging a debit card is a federal violation, so the default has
	 * to be the safe one.
	 *
	 * Resolved from the gateway at three points, so a card cannot slip through
	 * whichever way it was created: at card entry (Collect.js reports the
	 * category while the customer is still typing), lazily before first use if
	 * still unset, and by a one-off backfill for cards that predate this.
	 */
	@Column('varchar', {
		name: 'm_payment_cardCategory',
		nullable: true,
		length: 32,
	})
	public mPaymentCardCategory!: string | null;

	/**
	 * When the category above was last resolved from the gateway. Lets the
	 * backfill and the lazy resolver tell "never asked" from "asked, and the
	 * gateway had no answer", and gives an audit trail for a surcharge decision
	 * that has to be defensible to a card network.
	 */
	@Column('datetime', {
		name: 'm_payment_cardCategoryResolvedDate',
		nullable: true,
	})
	public mPaymentCardCategoryResolvedDate!: Date | null;

	/**
	 * PD-15014 — the card's BIN/IIN (first 6-8 digits), captured at
	 * tokenisation where the gateway or the entry form surfaces it. Null for
	 * ACH and for cards stored before this was captured.
	 *
	 * This is what makes a card CLASSIFIABLE after the fact: every funding
	 * lookup (Adyen /cardDetails, CyberSource BIN lookup, NMI card-type) takes
	 * a BIN, and without one a vaulted card can only be classified if its
	 * gateway can look inside its own vault. First 6-8 digits only — never a
	 * masked number, never more of the PAN (see normalizeCardBin).
	 */
	@Column('varchar', { name: 'm_payment_bin', nullable: true, length: 8 })
	public mPaymentBin!: string | null;

	@Column('varchar', { name: 'm_payment_name', nullable: true, length: 255 })
	public mPaymentName!: string | null;

	@Column('varchar', {
		name: 'm_payment_address1',
		nullable: true,
		length: 128,
	})
	public mPaymentAddress1!: string | null;

	@Column('varchar', {
		name: 'm_payment_address2',
		nullable: true,
		length: 128,
	})
	public mPaymentAddress2!: string | null;

	@Column('varchar', { name: 'm_payment_city', nullable: true, length: 64 })
	public mPaymentCity!: string | null;

	@Column('varchar', { name: 'm_payment_state', nullable: true, length: 16 })
	public mPaymentState!: string | null;

	@Column('varchar', { name: 'm_payment_zip', nullable: true, length: 16 })
	public mPaymentZip!: string | null;

	@Column('varchar', { name: 'm_payment_country', nullable: true, length: 16 })
	public mPaymentCountry!: string | null;

	/**
	 * PD-15878 — hydrated through a transformer because the column is a
	 * `tinyint(1)`: the MySQL driver returns 0/1, so this property held a
	 * NUMBER while its type said boolean. Every truthy reader coped, but an
	 * identity check (`=== true`) silently never matched — which is how the
	 * customer's designated default method stopped being offered first on
	 * the portal's invoice and multi-account payment surfaces. Converting
	 * here rather than at each reader keeps the declared type honest and
	 * stops the next identity check from reintroducing it.
	 */
	@Column('tinyint', {
		name: 'm_payment_isPrimary',
		width: 1,
		default: () => "'0'",
		transformer: {
			to: (value: boolean | number | null): number =>
				value === true || value === 1 ? 1 : 0,
			from: (value: boolean | number | null): boolean =>
				value === true || value === 1,
		},
	})
	public mPaymentIsPrimary!: boolean;

	@Column('int', { name: 'm_sm_payment_fk_client__id', unsigned: true })
	public mSmPaymentFkClientId!: number;

	@Column('date', { name: 'm_payment_expirationDate', nullable: true })
	public mPaymentExpirationDate!: string | null;

	@ManyToOne(() => ClientEntity, (client) => client.paymentMethods, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'm_sm_payment_fk_client__id', referencedColumnName: 'id' },
	])
	public mSmPaymentFkClient!: ClientEntity;

	/**
	 * PD-16204 — the processor configuration (the merchant account) this
	 * method's gateway token lives under.
	 *
	 * A gateway token is only usable by the account that issued it, so once
	 * a tenant runs a processor account per company, a card saved under one
	 * company's account cannot be charged through another's. Recording the
	 * issuing configuration here is what lets the charge path refuse that
	 * cleanly, and what keeps token maintenance (edit, delete, funding
	 * lookups) pointed at the right account even after the client's primary
	 * company changes.
	 *
	 * NULL for methods saved before this existed and for the legacy 1.0
	 * write path. Those resolve through the client's company at read time,
	 * with no cross-account refusal, which matches how they were always used.
	 * Cleared (never blocked) when the configuration is deleted, so an
	 * orphaned token degrades to that same legacy behaviour.
	 */
	@Column('int', {
		name: 'fk_payment_processor_config__id',
		nullable: true,
		unsigned: true,
	})
	public fkPaymentProcessorConfigId!: number | null;

	@ManyToOne(
		() => PaymentProcessorConfigEntity,
		(config) => config.paymentMethods,
		{
			onDelete: 'SET NULL',
			onUpdate: 'NO ACTION',
		},
	)
	@JoinColumn([
		{ name: 'fk_payment_processor_config__id', referencedColumnName: 'id' },
	])
	public fkPaymentProcessorConfig!: PaymentProcessorConfigEntity | null;

	@OneToMany(
		() => MPaymentTransactionEntity,
		(mPaymentTransaction) =>
			mPaymentTransaction.mPaymentFkMPaymentPaymentMethod,
		{},
	)
	public mPaymentTransactions!: Array<MPaymentTransactionEntity>;

	@OneToMany(() => OrderEntity, (order) => order.mSmPaymentFkMPaymentMethod, {})
	public orders!: Array<OrderEntity>;
}
